import { Pencil, RefreshCw } from 'lucide-react'
import { useEffect, useState } from 'react'
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Row,
  Table,
  Form
} from 'react-bootstrap'
import { useLocation, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import toast, { Toaster } from 'react-hot-toast'
import ModalAddCategoryItem from '../../../components/modal/items/ModalAddCategoryItem'
import ModalAddEditDimension from '../../../components/modal/items/ModalAddEditDimension'
import ModalEditQtyPallet from '../../../components/modal/items/ModalEditQtyPallet'
import ModalConfirmPackaging from '../../../components/modal/items/ModalConfirmPackaging'
import ModalEditBuItem from '../../../components/modal/items/ModalEditBuItem'
import api from '../../../config/AxiosInstance'
import Breadcrumb from '../../../components/Label/Breadcrumb'
import Loading from '../../../components/atoms/Loading'

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#6c757d',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: '4px'
}

const valueStyle = {
  fontSize: '1rem',
  fontWeight: '600',
  color: '#212529',
  marginBottom: '0'
}

const cardStyle = {
  border: 'none',
  borderRadius: '12px',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  backgroundColor: '#fff',
  marginBottom: '1.5rem'
}

const sectionHeaderStyle = {
  fontSize: '0.9rem',
  fontWeight: '800',
  color: '#9E9E9E',
  marginBottom: '1rem',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  display: 'flex',
  alignItems: 'center',
  gap: '10px'
}

const InfoItem = ({ label, value }) => (
  <div className="mb-4">
    <div style={labelStyle}>{label}</div>
    <div style={valueStyle}>{value || '-'}</div>
  </div>
)

const MasterItemDetailPage = () => {
  const { code } = useParams()
  const { state } = useLocation()
  const [detail, setDetail] = useState({})
  const [category, setCategory] = useState([])
  const [uoms, setUoms] = useState([])
  const [modalAddShow, setModalAddShow] = useState(false)
  const [modalDimension, setModalDimension] = useState(false)
  const [modalQtyPallet, setModalQtyPallet] = useState(false)
  const [modalConfirmPackaging, setModalConfirmPackaging] = useState(false)
  const [modalBu, setModalBu] = useState(false)
  const [selectedUom, setSelectedUom] = useState(null)
  const [newCode, setNewCode] = useState()
  const [isLoading, setIsLoading] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)

  useEffect(() => {
    loadData(code)
  }, [code])

  const loadData = async (code) => {
    setIsLoading(true)
    try {
      const res = await api.get(`master/items/${code}`)
      setDetail(res.data.data)
      setCategory(res.data.data.categories)
      setUoms(res.data.data.uoms || [])
      setNewCode(res.data.data.id)
    } catch (error) {
      const errMsg =
        error?.response?.data?.errors?.message || 'An error occurred'
      toast.error(errMsg, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleSync = async () => {
    setIsSyncing(true)
    try {
      await api.get(`/master/items/sync-item/${detail.id}`)
      toast.success('Synced successfully', {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
      loadData(code)
    } catch (error) {
      const errMsg =
        error?.response?.data?.errors?.message || 'An error occurred'
      toast.error(errMsg, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
    } finally {
      setIsSyncing(false)
    }
  }

  const handlePackagingChange = (uom) => {
    setSelectedUom(uom)
    setModalConfirmPackaging(true)
  }

  const handleSamplingChange = () => {
    const swalWithBootstrapButtons = Swal.mixin({
      customClass: {
        confirmButton: 'btn btn-primary-orange',
        cancelButton: 'btn btn-transparent'
      },
      buttonsStyling: false
    })

    swalWithBootstrapButtons
      .fire({
        title: 'Confirmation',
        text: 'Are you sure you want to change the sampling status for this item?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'Cancel',
        reverseButtons: true
      })
      .then(async (result) => {
        if (result.isConfirmed) {
          try {
            setIsLoading(true)
            await api.patch(`master/items/${detail.id}/sampling-status`)
            toast.success('Sampling status updated successfully', {
              position: 'top-right',
              style: {
                fontSize: '14px',
                fontWeight: '600'
              }
            })
            loadData(code)
          } catch (error) {
            setIsLoading(false)
            const errMsg =
              error?.response?.data?.errors?.message || 'An error occurred'
            toast.error(errMsg, {
              position: 'top-right',
              style: {
                fontSize: '14px',
                fontWeight: '600'
              }
            })
          }
        }
      })
  }

  if (isLoading) {
    return <Loading />
  }

  return (
    <>
      <Container className="mb-5 pb-5">
        <Row className="mb-4 align-items-center">
          <Col md={6} className="mt-3">
            <Breadcrumb
              label={detail.item_name}
              link="/master/items"
              back="Master Item"
              stateValue={state}
            />
          </Col>
          <Col md={6} className="text-end mt-3">
            <Button
              variant="warning"
              onClick={handleSync}
              disabled={isSyncing}
              className="text-white fw-bold shadow-sm"
              style={{ borderRadius: '8px' }}
            >
              <RefreshCw
                size={16}
                className={`me-2 ${isSyncing ? 'fa-spin' : ''}`}
              />
              {isSyncing ? 'Syncing...' : 'Sync to ERP'}
            </Button>
          </Col>
        </Row>

        <Row>
          <Col xs={12}>
            <Card style={cardStyle}>
              <Card.Body className="p-4">
                <Row>
                  <Col md={6}>
                    <InfoItem label="Item Name" value={detail.item_name} />
                  </Col>
                  <Col md={6}>
                    <InfoItem label="Item Code" value={detail.item_code} />
                  </Col>
                  <Col md={6}>
                    <InfoItem label="Item Group" value={detail.item_group} />
                  </Col>
                  <Col md={6}>
                    <InfoItem label="Description" value={detail.description} />
                  </Col>
                  <Col md={6}>
                    <InfoItem label="UOM Base" value={detail.uom} />
                    <div className="mt-3 mb-3">
                      <Form.Check
                        type="checkbox"
                        id="sampling-status"
                        label="Check if this item requires sampling on inbound"
                        checked={!!detail.is_need_sampling}
                        onChange={handleSamplingChange}
                        style={{
                          fontWeight: '600',
                          color: '#6c757d',
                          fontSize: '0.85rem'
                        }}
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <InfoItem
                      label="Primary Item Group"
                      value={detail.primary_item_group}
                    />
                  </Col>
                  <Col md={6}>
                    <div className="mb-4">
                      <div
                        style={labelStyle}
                        className="d-flex align-items-center"
                      >
                        Business Unit
                        <Pencil
                          size={14}
                          className="text-secondary ms-2 cursor-pointer"
                          onClick={() => setModalBu(true)}
                        />
                      </div>
                      <div style={valueStyle}>{detail.bu_name || '-'}</div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* List of UOM */}
            <Card style={cardStyle}>
              <Card.Body className="p-4">
                <div style={sectionHeaderStyle}>List of UOM</div>
                <Table hover responsive borderless className="mb-0">
                  <thead className="bg-light text-secondary">
                    <tr>
                      <th
                        className="py-3 ps-3"
                        style={{ fontSize: '0.8rem', fontWeight: '700' }}
                      >
                        UOM Name
                      </th>
                      <th
                        className="py-3"
                        style={{ fontSize: '0.8rem', fontWeight: '700' }}
                      >
                        UOM Conversion
                      </th>
                      <th
                        className="py-3"
                        style={{ fontSize: '0.8rem', fontWeight: '700' }}
                      >
                        Kemasan
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {uoms.length > 0 ? (
                      uoms.map((uom, idx) => (
                        <tr
                          key={idx}
                          style={{ borderBottom: '1px solid #f0f0f0' }}
                        >
                          <td className="ps-3 align-middle">{uom.uom_name}</td>
                          <td className="align-middle">
                            {uom.convertion_factor}
                          </td>
                          <td className="align-middle">
                            <Form.Check
                              type="switch"
                              id={`packaging-switch-${idx}`}
                              checked={uom.is_pack}
                              onChange={() => handlePackagingChange(uom)}
                              style={{ transform: 'scale(1.2)' }}
                            />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center text-muted py-4">
                          No UOMs found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>

            {/* QTY Items / Pallet */}
            <Card style={cardStyle}>
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div style={sectionHeaderStyle} className="mb-0">
                    QTY Items / Pallet
                    <Pencil
                      size={14}
                      className="text-secondary ms-2 cursor-pointer"
                      onClick={() => setModalQtyPallet(true)}
                    />
                  </div>
                </div>
                <div
                  className="fw-bold text-dark"
                  style={{ fontSize: '1.2rem' }}
                >
                  {detail.qty_unit ? detail.qty_unit : '0'}{' '}
                  {detail.uom_pack || 'Pack'}
                </div>
              </Card.Body>
            </Card>

            {/* Dimensions */}
            <Card style={cardStyle}>
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div style={sectionHeaderStyle} className="mb-0">
                    Dimensions
                    <Pencil
                      size={14}
                      className="text-secondary ms-2 cursor-pointer"
                      onClick={() => setModalDimension(true)}
                    />
                  </div>
                </div>
                <Row>
                  <Col md={6}>
                    <InfoItem label="P x L x T (cm)" value={detail.p_l_t} />
                  </Col>
                  <Col md={6}>
                    <InfoItem label="Volume (cm³)" value={detail.volume_item} />
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Categories */}
            <Card style={cardStyle}>
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div style={sectionHeaderStyle} className="mb-0">
                    Category
                    <Pencil
                      size={14}
                      className="text-secondary ms-2 cursor-pointer"
                      onClick={() => setModalAddShow(true)}
                    />
                  </div>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {category.length > 0 ? (
                    category.map((itemCategory) => (
                      <Badge
                        key={itemCategory.id}
                        bg="light"
                        className="text-dark border px-3 py-2 fw-normal"
                        style={{ fontSize: '0.9rem' }}
                      >
                        {itemCategory.name}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-muted">No categories assigned</span>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
      <ModalAddCategoryItem
        show={modalAddShow}
        onHide={() => setModalAddShow(false)}
        code={newCode}
        refreshData={() => loadData(code)}
        lastcategory={category}
      />
      <ModalAddEditDimension
        show={modalDimension}
        onHide={() => setModalDimension(false)}
        refreshData={() => loadData(code)}
        detail={detail}
      />
      <ModalEditQtyPallet
        show={modalQtyPallet}
        onHide={() => setModalQtyPallet(false)}
        refreshData={() => loadData(code)}
        detail={detail}
      />
      <ModalConfirmPackaging
        show={modalConfirmPackaging}
        onHide={() => setModalConfirmPackaging(false)}
        refreshData={() => loadData(code)}
        detail={detail}
        uom={selectedUom}
      />
      <ModalEditBuItem
        show={modalBu}
        onHide={() => setModalBu(false)}
        refreshData={() => loadData(code)}
        detail={detail}
      />
      <Toaster />
    </>
  )
}

export default MasterItemDetailPage
